import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from 'react';
import { createContextualCan, CanProps } from '@casl/react';
import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { useQuery } from '@tanstack/react-query';
import { apiCallerV2 } from '@/service/service/service';
import { PermissionTestMode, usePermissionTestMode } from './permissionTestMode';
import { PermissionRule } from '@/utils/access/permissions';
import { useAppSelector } from '../../redux';

export interface PermissionsResponse {
    permissions: PermissionRule[];
}

export type AppAbility = MongoAbility<[
    string,
    string
]>;

export const createDefaultAbility = (): AppAbility => {
    const { build } = new AbilityBuilder<AppAbility>(createMongoAbility);
    return build();
};
export const AbilityContext = createContext<AppAbility>(createDefaultAbility());
const PrivateCaslCan = createContextualCan(AbilityContext.Consumer);


export const Can = (props: CanProps<AppAbility>) => {
    const mode = usePermissionTestMode();

    if (mode === PermissionTestMode.ALLOW_ALL) {
        return <>{props.children}</>;
    }

    if (mode === PermissionTestMode.DENY_ALL) {
        return null;
    }

    return <PrivateCaslCan {...props} />;
};

export const useAbility = (): AppAbility => {
    const context = useContext(AbilityContext);
    if (!context) {
        throw new Error('useAbility must be used within an AbilityProvider');
    }
    return context;
};



export const buildAbilityFromPermissions = (permissions: PermissionRule[]): AppAbility => {
    const { can, cannot, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

    const permissionMap = new Map<string, PermissionRule>();

    permissions.forEach(({ action, entity, inverse, key }) => {
        const existing = permissionMap.get(key);
        if (!existing) {
            permissionMap.set(key, { action, entity, inverse, key });
        } else if (!existing.inverse && inverse) {
            permissionMap.set(key, { action, entity, inverse, key });
        }
    });
    for (const { action, entity, inverse } of permissionMap.values()) {
        const ruleFn = inverse ? cannot : can;
        ruleFn(action, entity);
    }

    return build();
};

export const loadPermissions = async (
    data: PermissionsResponse,
): Promise<AppAbility> => {
    try {
        return buildAbilityFromPermissions(data.permissions);
    } catch (error) {
        console.error('Failed to load permissions:', error);
        return createDefaultAbility();
    }
};

interface AbilityProviderProps {
    children: ReactNode;
}

export const AbilityProvider = ({ children }: AbilityProviderProps): JSX.Element => {
    const [ability, setAbility] = useState<AppAbility>(createDefaultAbility());
    const authenticated = useAppSelector(state => state.auth.authenticated);
    const mode = usePermissionTestMode();

    const { data } = useQuery({
        queryKey: ['fetchPermissionKeys', 'GET', 'v1/access_control/user_permission_list'],
        queryFn: apiCallerV2<PermissionsResponse>,
        enabled: authenticated
    });

    const updateAbilityContext = useCallback(async () => {
        try {
            let newAbility: AppAbility;
            if (mode === PermissionTestMode.ALLOW_ALL) {
                newAbility = buildAbilityFromPermissions([]);
                newAbility.can = () => true;
            } else if (mode === PermissionTestMode.DENY_ALL) {
                newAbility = buildAbilityFromPermissions([]);
                newAbility.can = () => false;
            } else {
                if (!data) return;
                newAbility = await loadPermissions(data);
            }

            setAbility(newAbility);
        } catch (err) {
            console.error('Error loading permissions:', err);
            setAbility(createDefaultAbility());
        }
    }, [data, mode]);

    useEffect(() => {
        updateAbilityContext();
    }, [updateAbilityContext]);

    return (
        <AbilityContext.Provider value={ability}>
            {children}
        </AbilityContext.Provider>
    );
};
